import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { IntegrationDisplayNameConfirmationDialogContent } from '@modules/integrationsV2/components/IntegrationDisplayNameConfirmationDialogContent';
import { IntegrationDisplayNameWarningAlert } from '@modules/integrationsV2/components/IntegrationDisplayNameWarningAlert';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, FormHelperText, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
  IntegrationType,
} from 'generated/graphql';
import { IntegrationNameErrorMessages } from 'modules/ui/Settings/Integrations/utils/integrationErrorMessages';
import { ChangeEvent, useState } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

export const WebhookFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { openModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [integrationName, setIntegrationName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [descriptionInput, setDescriptionInput] = useState('');

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.Webhook,
    integrationType,
    createdByUser: userID,
    inputData: {
      webhook: {
        displayName: integrationName,
      },
    },
    description: descriptionInput.trim(),
    onError: (error: Error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        setErrorMessage(IntegrationNameErrorMessages.DUPLICATE_NAME);
        return;
      }
      showErrorNotification({
        contentComponent: `Something went wrong${
          error.message ? `: ${error.message}` : '!'
        }`,
        props: {},
      });
    },
    onCompleted: () => {
      onDrawerClose();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.Webhook,
      });
    },
  });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Webhook,
    integrationType,
    hasValidFormFields: !!integrationName && !errorMessage,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      await createIntegration();
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: IntegrationChannelType.Webhook,
      });
    },
  });

  const onTextInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setIntegrationName(value);

    setErrorMessage(null);
  };

  const onSubmit = () => {
    if (integrationType !== IntegrationType.Feedback) {
      submitForm();
      return;
    }

    openModal({
      contentComponent: () => (
        <IntegrationDisplayNameConfirmationDialogContent
          displayName={integrationName}
          onConfirm={submitForm}
        />
      ),
      modalProps: {
        maxWidth: 'md',
        fullWidth: false,
        PaperProps: {
          elevation: 3,
          sx: {
            width: '700px',
          },
        },
      },
    });
  };

  const isLoading = IsLoading(status);

  const renderInputHelpText = () => {
    return errorMessage ? (
      <FormHelperText
        sx={{
          textAlign: 'left',
          color: 'error.light',
        }}
      >
        {errorMessage}
      </FormHelperText>
    ) : null;
  };

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              error={Boolean(errorMessage)}
              label="Webhook Integration Name"
              placeholder="Enter Integration Name"
              variant="outlined"
              value={integrationName}
              onChange={onTextInputChange}
              disabled={isLoading}
              helperText={renderInputHelpText()}
              required
            />
            {integrationType === IntegrationType.Feedback && (
              <IntegrationDisplayNameWarningAlert />
            )}
          </Stack>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={onSubmit}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </Stack>
  );
};
